/**
 * Feature utility functions for building ARIA tree structure
 *
 * @package @alchemiesolns/svg-interactive
 */

import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { WorkspaceFeature } from '@alchemiesolns/feature-extractor-compat';
import {
  nameFeature,
  indexElementsInScope,
  classifyFeature,
  stableFeatureId,
  // PIPH-118: For bridged bicyclic atom ordering
  computeBridgeheads,
  computeBicyclicPaths,
  buildOrderedAtomList,
} from '@alchemiesolns/feature-extractor';
import type { FeatureType, MolecularGraph, SerializationResult } from '@alchemiesolns/feature-extractor';
import type { AtomLeaf, BondLeaf, BondEndpointLeaf, LonePairLeaf, FeatureNode } from './types';
import { isHydrogen } from './atomUtils';
import { createAtomLeaf } from './atomFactory';
import { createBondLeafForAtom } from './bondUtils';

/**
 * Primary structural feature types — processed before functional groups so
 * that shared atoms get indices from the more-discriminating primary scope.
 * @see PIPH-104 follow-up
 */
const PRIMARY_FEATURE_TYPES: ReadonlySet<FeatureType> = new Set<FeatureType>([
  'chain',
  'ring',
  'bridged_bicyclic',
  'atom_cluster',
]);

/**
 * Determine feature category from feature name
 */
export function getFeatureCategory(featureName: string): FeatureNode['category'] {
  if (featureName.startsWith('ring')) {
    return 'ring';
  }
  if (featureName.startsWith('chain')) {
    return 'chain';
  }
  // Most other features are functional groups
  const functionalGroups = [
    'alkene', 'alkyne', 'alcohol', 'ether', 'sulfide', 'thiol',
    'amine', 'carbonyl', 'alkylHalide', 'nitro', 'nitrile'
  ];
  if (functionalGroups.some(fg => featureName.startsWith(fg))) {
    return 'functional-group';
  }
  return 'other';
}

/**
 * Build feature nodes from extracted features
 *
 * @param graph - PIPH-118: MolecularGraph for bridged bicyclic atom ordering
 * @param indexToAtomId - PIPH-118: Mapping from atom indices to workspace IDs
 * @param serialized - PIPH-309 ordering: SerializationResult whose
 *                     `SerializedFeature.atomIndices` gives the IUPAC-reordered
 *                     atom order for chain / ring primaries. When provided,
 *                     non-bridged primary features render their atom children
 *                     in this order; absent or unmatched features fall back to
 *                     parse order.
 */
export function buildFeatureNodes(
  workspaceFeatures: WorkspaceFeature[],
  workspace: MolecularWorkspace,
  moleculeAtomIds: Set<string>,
  moleculeBondIds: Set<string>,
  atomsById: Map<string, AtomLeaf>,
  bondsById: Map<string, BondLeaf>,
  endpointsById: Map<string, BondEndpointLeaf>,
  lonePairsById: Map<string, LonePairLeaf>,
  featuresById: Map<string, FeatureNode>,
  parentById: Map<string, string>,
  moleculeId: string,
  graph?: MolecularGraph,
  indexToAtomId?: string[],
  serialized?: SerializationResult,
): { features: FeatureNode[]; claimedAtomIds: Set<string>; claimedBondIds: Set<string> } {
  const features: FeatureNode[] = [];
  const claimedAtomIds = new Set<string>();
  const claimedBondIds = new Set<string>();

  // Filter features to only those with atoms in this molecule
  const moleculeFeaturesUnsorted = workspaceFeatures.filter(wf => {
    return wf.atomIds.some(id => moleculeAtomIds.has(id));
  });

  // PIPH-104 follow-up: Process primary features (chain/ring/bridged_bicyclic/
  // atom_cluster) before functional groups. This is the invariant the
  // inheritance pass below (and the cross-feature other-atom fallback) relies
  // on: when a functional-group feature looks at `atomsById` for an atom it
  // shares with a primary, the primary's more-discriminating entry is
  // guaranteed to be there. Sort is stable so intra-group feature ordering
  // from the extractor is preserved.
  const moleculeFeatures = [...moleculeFeaturesUnsorted].sort((a, b) => {
    const aPrimary = PRIMARY_FEATURE_TYPES.has(classifyFeature(a.feature)) ? 0 : 1;
    const bPrimary = PRIMARY_FEATURE_TYPES.has(classifyFeature(b.feature)) ? 0 : 1;
    return aPrimary - bPrimary;
  });

  // Skip "atom", "chg", "lp", "bondedH" features - these are not structural features
  const skipFeatures = ['atom', 'chg', 'lp', 'bondedH', 'molecule', 'diatomic', 'protonated'];

  for (const wf of moleculeFeatures) {
    const featureName = wf.feature.name;

    // Skip non-structural features
    if (skipFeatures.some(skip => featureName.startsWith(skip))) {
      continue;
    }

    const featureId = wf.feature.id || `feature-${features.length + 1}`;

    // Get atoms in this feature that belong to this molecule
    const featureAtomIds = wf.atomIds.filter(id => moleculeAtomIds.has(id));
    if (featureAtomIds.length === 0) continue;

    // Collect non-hydrogen atoms in iteration order. Hydrogens are claimed
    // up-front so they don't appear as "unclaimed" later; they'll be added as
    // children of their parent atoms during atom-leaf creation.
    let nonHAtomsInOrder: Array<{atomId: string; atom: ReturnType<typeof workspace.getAtom>}> = [];
    for (const atomId of featureAtomIds) {
      if (isHydrogen(workspace, atomId)) {
        claimedAtomIds.add(atomId);
        continue;
      }
      const atom = workspace.getAtom(atomId);
      if (!atom) continue;
      nonHAtomsInOrder.push({atomId, atom});
    }

    const featureType = classifyFeature(wf.feature);

    // PIPH-309 ordering: for non-bridged primary features (chain / ring /
    // atom_cluster), reorder atom children to match the serializer's
    // IUPAC-reordered `SerializedFeature.atomIndices`. The label resolver
    // already returns IUPAC-numbered names; this brings the tree's *display
    // order* into the same numbering so the chain renders Carbon-1 first,
    // Carbon-4 last (instead of parse order). Bridged bicyclics are handled
    // by the PIPH-118 block below — `serializeFeatures` keeps the same
    // path-based order there, so we leave that block in place rather than
    // collapsing both paths.
    if (
      serialized &&
      indexToAtomId &&
      featureType !== 'bridged_bicyclic' &&
      PRIMARY_FEATURE_TYPES.has(featureType)
    ) {
      const featureStableId = stableFeatureId(wf.feature);
      const serializedFeature = serialized.features.find(sf => sf.id === featureStableId);
      if (serializedFeature) {
        // Join workspace atom ids → graph indices once, then walk
        // `serializedFeature.atomIndices` to pluck entries in IUPAC order.
        const atomIdToIndex = new Map<string, number>();
        for (let i = 0; i < indexToAtomId.length; i++) {
          atomIdToIndex.set(indexToAtomId[i], i);
        }
        const byIndex = new Map<number, typeof nonHAtomsInOrder[number]>();
        for (const entry of nonHAtomsInOrder) {
          const idx = atomIdToIndex.get(entry.atomId);
          if (idx !== undefined) byIndex.set(idx, entry);
        }
        const reordered: typeof nonHAtomsInOrder = [];
        for (const idx of serializedFeature.atomIndices) {
          const entry = byIndex.get(idx);
          if (entry) {
            reordered.push(entry);
            byIndex.delete(idx);
          }
        }
        // Append any stragglers (defensive — shouldn't fire when the
        // serializer and the workspace feature agree on atom membership).
        for (const entry of byIndex.values()) reordered.push(entry);
        if (reordered.length === nonHAtomsInOrder.length) {
          nonHAtomsInOrder = reordered;
        }
      }
    }

    // PIPH-118: Reorder atoms for bridged bicyclic features to match path order.
    // This ensures HTML tree atom labels match feature description labels.
    if (featureType === 'bridged_bicyclic' && graph && indexToAtomId) {
      // Build atom ID to graph index mapping
      const atomIdToIndex = new Map<string, number>();
      for (let i = 0; i < indexToAtomId.length; i++) {
        atomIdToIndex.set(indexToAtomId[i], i);
      }

      // Compute bridgeheads and paths using the same algorithm as serializeFeatures
      const bridgeheads = computeBridgeheads(wf.feature, graph);
      const paths = computeBicyclicPaths(wf.feature, graph, bridgeheads);

      if (paths) {
        // Build ordered atom list following path order
        // We need to create GraphAtom-like objects with indices for buildOrderedAtomList
        const atomsWithIndices = nonHAtomsInOrder.map(e => ({
          ...e,
          index: atomIdToIndex.get(e.atomId) ?? -1,
        }));

        // Filter out any atoms without valid indices
        const validAtoms = atomsWithIndices.filter(a => a.index >= 0);

        if (validAtoms.length === nonHAtomsInOrder.length) {
          const reordered = buildOrderedAtomList(
            validAtoms as unknown as import('@alchemiesolns/feature-extractor').GraphAtom[],
            paths.mainRingPaths,
            paths.bridgeAtomIndices
          );
          // Extract just atomId and atom from reordered list
          nonHAtomsInOrder = reordered.map(r => ({
            atomId: (r as unknown as {atomId: string}).atomId,
            atom: (r as unknown as {atom: ReturnType<typeof workspace.getAtom>}).atom,
          }));
        }
      }
    }

    // Compute per-element labeling indices using the shared naming module
    // (PIPH-79). Per-feature semantics: each feature scope produces its own
    // index, so a carbon shared by a chain and an alcohol gets "Carbon-3" in
    // the chain's tree subtree and just "Carbon" in the alcohol's. No
    // cross-feature inheritance — the per-feature label is authoritative.
    //
    // PIPH-196: Ring-like features (ring, bridged_bicyclic) override the
    // per-element rule with a positional rule: every atom gets a 1-indexed
    // suffix matching its position in the feature's atom walk, regardless
    // of element duplication. Mirrors serializeFeatures.atomLabels.
    const isRingLike =
      featureType === 'ring' || featureType === 'bridged_bicyclic';
    const indexEntries = isRingLike
      ? nonHAtomsInOrder.map((_, i) => ({
          elementIndex: i + 1,
          totalOfElement: nonHAtomsInOrder.length,
        }))
      : indexElementsInScope(nonHAtomsInOrder.map(e => e.atom!.symbol));

    // Create atom leaves for this feature
    // Atoms can appear in multiple features - each gets a feature-scoped ID
    const featureAtomLeaves: AtomLeaf[] = [];
    for (let i = 0; i < nonHAtomsInOrder.length; i++) {
      const {atomId, atom} = nonHAtomsInOrder[i];
      if (!atom) continue;
      const {elementIndex, totalOfElement} = indexEntries[i];

      // Create feature-scoped ID for this atom instance
      const treeId = `${featureId}:${atomId}`;

      const leaf = createAtomLeaf(
        atomId,
        treeId,  // Feature-scoped tree ID
        atom.symbol,
        atom.charge,
        atom.lonePairs || 0,
        lonePairsById,
        bondsById,
        endpointsById,
        parentById,
        featureId,
        workspace,
        atomsById,
        elementIndex,
        totalOfElement
      );
      featureAtomLeaves.push(leaf);
      atomsById.set(treeId, leaf);  // Index by feature-scoped ID
      claimedAtomIds.add(atomId);  // Track original ID for unclaimed atoms
    }

    // Populate bondNodes for each atom (bonds to non-H atoms)
    // Bonds are now children of atoms, not siblings in the feature
    for (const atomLeaf of featureAtomLeaves) {
      const atomId = atomLeaf.atomId;

      for (const bondId of moleculeBondIds) {
        const bond = workspace.getBond(bondId);
        if (!bond) continue;

        // Skip bonds involving hydrogens - these are parent-child relationships
        if (bond.a1.symbol === 'H' || bond.a2.symbol === 'H') continue;

        // Check if this bond involves the current atom
        let otherAtomId: string | null = null;
        if (bond.a1.id === atomId) {
          otherAtomId = bond.a2.id;
        } else if (bond.a2.id === atomId) {
          otherAtomId = bond.a1.id;
        }

        if (!otherAtomId) continue;

        // PIPH-104: Skip self-bonds (bond where parent atom === other atom)
        if (otherAtomId === atomId) continue;

        // Include ALL bonds for heavy atoms, not just those within the same feature
        // This ensures connection bonds between features are navigable
        const otherAtom = workspace.getAtom(otherAtomId);
        if (!otherAtom) continue;

        // Look up other atom's index info for bond label.
        // First try this feature's scope, then fall back to ANY feature that
        // contains the other atom (PIPH-104: ensures cross-feature bonds have indices)
        const otherAtomTreeId = `${featureId}:${otherAtomId}`;
        let otherAtomLeaf = atomsById.get(otherAtomTreeId);
        if (!otherAtomLeaf) {
          // PIPH-120: First check if the other atom is in THIS feature's atom list.
          // This ensures we get the correct elementIndex for atoms within the same
          // feature, avoiding bugs where the wrong index from another feature is used.
          otherAtomLeaf = featureAtomLeaves.find(leaf => leaf.atomId === otherAtomId);
        }
        if (!otherAtomLeaf) {
          // Cross-feature bond: the other atom lives in a different feature.
          // We process primary features first (see PRIMARY_FEATURE_TYPES sort
          // above), so the FIRST matching leaf in `atomsById` belongs to the
          // other atom's highest-priority primary feature — which is exactly
          // the "default feature" we want to name a cross-feature neighbor
          // by. This matches the resolver's `defaultFeatureLabel` policy in
          // feature-extractor without needing the SerializationResult here.
          for (const leaf of atomsById.values()) {
            if (leaf.atomId === otherAtomId) {
              otherAtomLeaf = leaf;
              break;
            }
          }
        }

        // Create bond leaf as child of this atom
        const bondLeaf = createBondLeafForAtom(
          bondId,
          atomId,
          otherAtomId,
          atomLeaf.symbol,
          otherAtom.symbol,
          bond.order,
          bond.bondType,
          atomLeaf.id,  // treeIdPrefix (feature-scoped atom ID)
          bondsById,
          endpointsById,
          parentById,
          atomLeaf.id,  // parentTreeId
          atomLeaf.elementIndex,  // parent atom index for endpoint label
          atomLeaf.totalOfElement,  // parent atom total for endpoint label
          otherAtomLeaf?.elementIndex,  // other atom index for bond/endpoint label
          otherAtomLeaf?.totalOfElement,  // other atom total for bond/endpoint label
          atomLeaf.x,  // PIPH-74: parent atom X position
          atomLeaf.y,  // PIPH-74: parent atom Y position
          otherAtom.x,  // PIPH-74: other atom X position
          otherAtom.y,  // PIPH-74: other atom Y position
          atomLeaf.hasStereoContext  // PIPH-74: include direction in label
        );

        atomLeaf.bondNodes.push(bondLeaf);
        claimedBondIds.add(bondId);
      }
    }

    const featureNode: FeatureNode = {
      type: 'feature',
      id: featureId,
      stableId: stableFeatureId(wf.feature),
      label: nameFeature(featureName, wf.feature.uniqueName),
      category: getFeatureCategory(featureName),
      atoms: featureAtomLeaves,
      children: [], // Child features could be added here in the future
    };

    features.push(featureNode);
    featuresById.set(featureId, featureNode);
    parentById.set(featureId, moleculeId);
  }

  return { features, claimedAtomIds, claimedBondIds };
}
